import { SignIn } from '@clerk/react';
import { Landmark } from '@vitness/icons';
import { AuthPage, useVitnessClerkAppearance } from '@vitness/auth';
import { useUnscaled } from '../lib/useUiScale';

/**
 * The sign-in screen.
 *
 * `AuthPage` is the same shell the showcase app uses — vendored rather than
 * reimplemented, so the two stay in step. It owns the brand column and the card;
 * Clerk owns everything inside it. `useVitnessClerkAppearance` reads the live
 * theme (accent, light/dark) and hands Clerk matching tokens, which is what
 * stops the embedded form looking like a third-party iframe dropped on the page.
 */
export function SignInRoute() {
  const appearance = useVitnessClerkAppearance();
  // AuthPage is a full-viewport composition; the dashboard's 125% zoom makes it
  // overflow and scroll. Pinned to 1:1 for as long as this route is mounted.
  useUnscaled();

  return (
    <AuthPage
      brandName="vudget"
      // Lucide's columned bank building. Reads as "money institution" at 20px,
      // where a piggy bank would read as children's savings.
      brandIcon={<Landmark size={20} strokeWidth={2} />}
      // Empty on purpose: "vudget | VUDGET" is the same word twice, so the
      // lockup drops the divider and the mono label when this is blank.
      applicationName=""
      eyebrow="Conscious Spending Plan"
      headline={<>Every dollar.<br />On purpose.</>}
      description={
        <>
          Your plan, your numbers, synced from Notion and yours alone. Sign in to
          pick up where the last projection left off.
        </>
      }
      footer="Private by default — this dashboard is for one person."
    >
      {/*
        Path-routed, matching the showcase. The `/sign-in/*` splat in App.tsx is
        what lets Clerk own its own sub-steps. `signUpUrl` is deliberately absent:
        this is a single-person dashboard, so there is nothing to sign up for.
      */}
      <SignIn
        appearance={appearance}
        routing="path"
        path="/sign-in"
        forceRedirectUrl="/"
      />
    </AuthPage>
  );
}
